/**
 * voice-v8 — welchen Weg die Sprechtaste nimmt: nativ oder Web.
 * (24.09.2026, Ticket 2202)
 *
 * ## Woher das kommt
 *
 * Läuft die Tagesaufnahme, ist das Mikro der Sprechtaste NIE warm: das
 * Vorwärmen steigt aus, solange die Tagesaufnahme das Mikro hält (Ticket 570),
 * und jeder Druck zahlt den vollen kalten Start. Gemessen am 21.09. abends am
 * iPhone: 1.521, 1.539 und 1.749 ms, jedes Mal mit
 * `kalt=nicht-gewaermt:tagesaufnahme`. Der Anfang des Satzes fehlt.
 *
 * Der native Weg (`holePttNativ`, 05.09.) nimmt das Mikro, das die
 * Tagesaufnahme ohnehin offen hält — ein zweiter Rekorder auf derselben
 * Sitzung, ohne Profilwechsel und ohne Pause VOR dem Start. Er ist seit dem
 * 08.09. abgeschaltet, weil ihm die Live-Mitschrift fehlt (Martin: „warum
 * der Text sich nicht mehr erzeugt und es danach sehr lange dauert?").
 *
 * ⚠️ NACH dem Start hält er die Tagesaufnahme an wie der Web-Weg (ptt.ts,
 *    halteTagesaufnahmeAn) — sonst schnitte sie jeden Satz an Vespera mit,
 *    und die Alltag-Auswertung legte daraus doppelte Tickets und
 *    Erinnerungsfragen an (Befund der Prüfung, 24.09.2026).
 *
 * ⚠️ „Danach dauert es länger" ist eine Annahme, keine Messung. Seit dem
 *    04.09. erkennt der Server die Tondatei auf BEIDEN Wegen selbst; der
 *    Live-Text ist dort nur der Rückfall (voice-command.ts,
 *    `liveAlsRueckfall`). Ob nativ nach dem Loslassen länger gewartet wird,
 *    zeigt erst `[ptt-text]`.
 *
 * ## Martins Entscheidung (24.09.2026, Variante C)
 *
 * „Erst messen — ich schalte ihn probeweise ein und vergleiche eine Woche."
 * Also: NUR während einer laufenden Tagesaufnahme, NUR in der nativen App,
 * mit festem Enddatum. Danach ist der Schalter von selbst wieder aus — kein
 * Aufräumen nötig, und die Messzeilen laufen weiter, so dass die Woche danach
 * der Vergleich ist.
 *
 * ⚠️ Nur für den Hauptnutzer. Die Frage ging an Martin („du verlierst die
 *    Live-Mitschrift"); Albert hat sie nie gesehen, und ihm fiele die
 *    fehlende Mitschrift auf, ohne dass ihn jemand gefragt hat. Der sparsame
 *    Fall ist die Vorgabe (siehe hauptnutzer.ts).
 *
 * ⚠️ Diese Datei entscheidet nur über die REGEL. Was sich erst am Mikrofon
 *    zeigt — ein veralteter Zustand der Tagesaufnahme, eine eingetragene
 *    Pause, eine alte App ohne native Griffe, ein gescheiterter Start —,
 *    prüft `starteNativ` in ptt.ts und schreibt es in dieselbe Messzeile
 *    (`wahl=`).
 */

/**
 * Bis wann die Probe läuft: einschließlich Donnerstag, 01.10.2026 (Wien).
 *
 * ⚠️ Sieben Tage ab der Auslieferung am 24.09. Wird später ausgeliefert,
 *    gehört dieses Datum mitgeschoben — sonst wird die Woche kürzer.
 */
export const NATIV_PROBE_BIS = Date.parse("2026-10-02T00:00:00+02:00");

/** Der Schlüssel des Entwicklerschalters (siehe shared/einstellungen-felder.ts). */
export const NATIV_SCHALTER = "voice-v8.pttNativ";

/**
 * Was die Aufnahmekarte statt der Mitschrift sagt, solange der native Weg
 * aufnimmt. (Ticket 2607)
 *
 * Martin, 25.09.2026, 12:06: *„Keine mitlaufende Live-Transkription mehr —
 * nur ‚Ich höre zu' bis zum Loslassen."* Im Journal lagen davor genau drei
 * native Drücke der Probe (11:56, 12:02, 12:05, alle `live=nein weg=nativ
 * tages=ja`), dazwischen Web-Drücke mit Mitschrift. Der Satz der Karte war
 * wahr („der Text kommt nach dem Loslassen"), nannte aber keinen Grund —
 * und stand groß wie ein erkannter Satz da. So sah der Preis von Variante C
 * aus wie ein Fehler.
 *
 * ⚠️ Der Grund ist immer die Tagesaufnahme: `wegWahl` wählt nativ nur,
 *    solange sie läuft (auch mit dem Entwicklerschalter).
 */
export const NATIV_OHNE_MITSCHRIFT =
  "Die Tagesaufnahme läuft – darum kommt die Mitschrift erst nach dem Loslassen.";

/**
 * Warum dieser Weg. Steht als `wahl=` in `[ptt-t]`, `[ptt-ende]` und
 * `[ptt-text]`.
 *
 * ⚠️ Nur Kleinbuchstaben und Bindestrich: der Server liest Felder dieser
 *    Zeilen mit `[a-z-]+` (latenz-store.ts), ein Umlaut risse den Wert ab.
 */
export type WegGrund =
  /* nativ */
  | "probe"
  | "schalter"
  /* Web — aus der Regel */
  | "browser"
  | "ohne-tagesaufnahme"
  | "anruf"
  | "abgeschaltet"
  | "gemieden"
  | "anderes-konto"
  | "probe-vorbei"
  /* Web — erst am Mikrofon erkannt (ptt.ts, starteNativ) */
  | "abgleich"
  | "pausengrund"
  | "alte-app"
  | "startfehler";

export interface WegLage {
  jetzt: number;
  /** Läuft die Seite in der nativen Hülle? */
  nativeApp: boolean;
  /** Nimmt die Tagesaufnahme gerade auf (Zustand „recording")? */
  tagesaufnahme: boolean;
  /** Läuft ein Anruf? Der braucht das Mikrofon allein. */
  anruf: boolean;
  /** Ist Martin angemeldet (hauptnutzer.ts)? */
  hauptnutzer: boolean;
  /** Wert des Entwicklerschalters: "1" immer nativ, "0" nie, sonst die Probe. */
  schalter: string | null;
  /** Bis wann der native Weg nach „nichts verstanden" gemieden wird. */
  gemiedenBis: number;
}

interface WegWahl {
  nativ: boolean;
  grund: WegGrund;
}

/**
 * Die Regel. Rein — die Zeit und alle Zustände werden hereingereicht.
 *
 * ⚠️ Die Reihenfolge ist Absicht: erst was den nativen Weg technisch
 *    ausschließt (Hülle, Tagesaufnahme, Anruf), dann der Wille (Schalter
 *    aus, gemieden), dann was ihn erlaubt (Schalter an, Probe).
 */
export function wegWahl(l: WegLage): WegWahl {
  if (!l.nativeApp) return { nativ: false, grund: "browser" };
  /*
   * ⚠️ „recording", nicht „pausiert" oder „Absicht in der Lücke": Am 08.09.
   *    lief eine native Aufnahme in eine pausierte Sitzung, und die Rettung
   *    riss die Sitzung mitten im Satz ab (native-recorder.ts,
   *    micAnmeldungen). Außerhalb einer laufenden Aufnahme bleibt alles,
   *    wie es war.
   */
  if (!l.tagesaufnahme) return { nativ: false, grund: "ohne-tagesaufnahme" };
  if (l.anruf) return { nativ: false, grund: "anruf" };
  if (l.schalter === "0") return { nativ: false, grund: "abgeschaltet" };
  if (l.jetzt < l.gemiedenBis) return { nativ: false, grund: "gemieden" };
  if (l.schalter === "1") return { nativ: true, grund: "schalter" };
  if (!l.hauptnutzer) return { nativ: false, grund: "anderes-konto" };
  if (l.jetzt >= NATIV_PROBE_BIS) return { nativ: false, grund: "probe-vorbei" };
  return { nativ: true, grund: "probe" };
}

/**
 * Die Messzeile „Loslassen bis Text". (24.09.2026, Ticket 2202)
 *
 * Der zweite Teil des Handels: der native Weg gewinnt am Start — ob er nach
 * dem Loslassen verliert, ist die offene Frage (siehe oben: der Server
 * erkennt die Datei auf beiden Wegen; der Web-Weg wartet dafür vor dem
 * Senden auf den Abschluss seiner Mitschrift). Diese Zeile misst es — je
 * Druck einmal, mit demselben Weg-Anhang wie `[ptt-t]` (pttTiming.wegZeile).
 *
 *   ergebnis  text | leer („nichts verstanden") | fehler
 *   warte     Loslassen bis Text, auf der Uhr des Geräts
 *   live      ging die Mitschrift mit? Nativ nie.
 *
 * ⚠️ Der Name ist Absicht: der Server erkennt einen Mikro-Start an der
 *    Zeichenfolge `[ptt-t]` (latenz-store.ts, lesePttStart). `[ptt-text]`
 *    enthält sie nicht und zählt dort nie als Start.
 */
export function textWarteZeile(t: {
  ergebnis: "text" | "leer" | "fehler";
  warteMs: number;
  live: boolean;
  weg: string;
}): string {
  const anhang = t.weg ? ` ${t.weg}` : "";
  return `[ptt-text] ergebnis=${t.ergebnis} warte=+${Math.max(0, Math.round(t.warteMs))}ms live=${t.live ? "ja" : "nein"}${anhang}`;
}

/**
 * Loslassen und Weg EINES Drucks — für `[ptt-text]`. (24.09.2026, Ticket 2202)
 *
 * Bisher stand die Regel „je Druck genau eine Zeile" als Zähler in
 * pttSendBlob (app.ts) und war nur am Wortlaut prüfbar (Befund der
 * Prüfung: Form statt Sache). Jetzt trägt der Druck selbst, ob er schon
 * gemeldet ist — und die Regel ist hier als Verhalten geprüft
 * (ptt-nativ-probe-2202.test.ts).
 */
export interface TextMessung {
  /** performance.now() beim Loslassen. */
  losAt: number;
  /**
   * pttTiming.wegZeile() — gelesen erst NACH dem Warten auf den Start
   * (pttFinishAndSend): Wer loslässt, während der native Start noch über
   * die Brücke läuft, hätte sonst einen Druck ohne Weg gezählt.
   */
  weg: string;
  /** Schon gemeldet? Dann kommt für diesen Druck keine zweite Zeile. */
  gemeldet: boolean;
  /**
   * Ticket 2668 (27.09.2026): öffnete dieser Druck das Nachlausch-Fenster,
   * ohne dass Sprache belegt war? Endet er ohne Text, schließt er still
   * (app.ts, pttLeerStill); mit belegter Sprache bleibt die Rückmeldung.
   */
  nachlauschOhneSprache?: boolean;
}

/** Die Uhr läuft ab dem Loslassen. */
export function neueTextMessung(losAt: number): TextMessung {
  return { losAt, weg: "", gemeldet: false };
}

/**
 * Die `[ptt-text]`-Zeile dieses Drucks — oder null.
 *
 * null heißt: kein Druck (Erneut senden, Satz nach dem Wach-Wort — dort gibt
 * es kein Loslassen, eine Wartezeit wäre erfunden) oder schon gemeldet.
 */
export function textMessungAbschliessen(
  m: TextMessung | undefined,
  t: { ergebnis: "text" | "leer" | "fehler"; live: boolean; jetzt: number },
): string | null {
  if (!m || m.gemeldet) return null;
  m.gemeldet = true;
  return textWarteZeile({
    ergebnis: t.ergebnis,
    warteMs: t.jetzt - m.losAt,
    live: t.live,
    weg: m.weg,
  });
}
